import type {
  CartLines,
  CartProductDetail,
  CartVariant,
} from "../../cart/cart";
import { findCartLine } from "../../cart/purchase";
import {
  MAX_QUANTITY,
  missingOptions,
  parseQuantity,
  selectedVariant,
  valueSoldOut,
  type CartSelection,
} from "../../cart/selection";
import { formatPrice } from "../../conversation/reply-attachments";
import * as copy from "../../paraglide/messages.js";
import type { ResolvedProduct, WidgetCartReceipt } from "../message";
import type {
  CartOptionView,
  CartPrimaryAction,
  CartStatus,
} from "./cart-sheet";

/** checking：结果没法确认之后重新读购物车核对，不再发加购请求。 */
export type AddPhase =
  "idle" | "adding" | "checking" | "failed" | "rejected" | "unconfirmed";
export type EditPhase =
  "idle" | "saving" | "failed" | "rejected" | "unconfirmed";

/** 加购面板：选到哪个变体、数量框里写了什么、这次加购走到哪一步。 */
export interface AddSheetState {
  product: ResolvedProduct;
  detail: CartProductDetail;
  selection: CartSelection;
  qtyText: string;
  /** 数量刚被自动改过时的说明，占数量框下那一行。 */
  note?: string;
  phase: AddPhase;
  /** Shopify 拒绝时它自己的说明。 */
  message?: string;
  /** 结果没法确认时写入前这一行的件数：Check again 按它核对；没有就核对不了。 */
  before?: number;
}

export type LineRead =
  | { status: "loading" }
  | { status: "ok"; key: string; quantity: number }
  | { status: "missing" }
  | { status: "failed" };

/** 改数量面板：回执对应的那一行购物车读到没有、数量框里写了什么、这次改动走到哪一步。 */
export interface EditSheetState {
  receipt: WidgetCartReceipt;
  line: LineRead;
  draft: string;
  note?: string;
  phase: EditPhase;
  /** 上一次要改成的数：失败后 Retry 按它重来。 */
  tried?: number;
  message?: string;
}

/** 面板算件数和价格用到的购物车信息，都以 Shopify 返回为准。 */
export interface CartFacts {
  /** 打开面板时读到的购物车；没读到就不知道车里已有几件，也不标总价。 */
  lines?: CartLines;
  /** 变体 id → Shopify 告诉过的这一行最多能放几件。 */
  known: Record<number, number>;
  locale?: string;
}

/** 取值都很短（尺码、鞋码）就排成等宽网格，否则按文字宽度换行排。 */
const GRID_VALUE_LENGTH = 4;

function listOf(names: string[]): string {
  try {
    return new Intl.ListFormat("en", { type: "conjunction" }).format(names);
  } catch {
    return names.join(", ");
  }
}

export function inCartOf(facts: CartFacts, variantId: number): number {
  return facts.lines
    ? (findCartLine(facts.lines, variantId)?.quantity ?? 0)
    : 0;
}

/** 这个变体还能再加几件；Shopify 没说过上限就按数量框的上限算。 */
export function roomOf(facts: CartFacts, variantId: number): number {
  const cap = facts.known[variantId];
  return cap === undefined
    ? MAX_QUANTITY
    : Math.max(0, cap - inCartOf(facts, variantId));
}

/** 数量被压到能加的件数时，数量框下面那行说明。 */
export function clampNote(
  facts: CartFacts,
  variantId: number,
  to: number,
): string {
  const inCart = inCartOf(facts, variantId);
  return inCart > 0
    ? copy.widget_cart_clamp_in_cart({ count: to, inCart })
    : copy.widget_cart_clamp_available({
        count: to,
        available: facts.known[variantId] ?? to,
      });
}

export interface AddSheetModel {
  variant?: CartVariant;
  /** 选中的变体名，如 White / M；没有选项的商品是空串。 */
  label: string;
  quantity?: number;
  room: number;
  /** 不能提交的原因；有它按钮就禁用。 */
  reason?: string;
  soldOut: boolean;
  busy: boolean;
  title: string;
  price: string;
  imageUrl?: string;
  options: CartOptionView[];
  hint: string;
  decreaseDisabled: boolean;
  increaseDisabled: boolean;
  status: CartStatus;
  primary: Omit<CartPrimaryAction, "onClick">;
  /** 结果没法确认：按钮旁并排摆查看购物车。 */
  showCart: boolean;
  /** 结果没法确认：按钮只重新读购物车核对，不再发加购请求，免得加两遍。 */
  recheck: boolean;
}

/** 加购面板上显示的一切：由面板状态和购物车信息算出，不碰网络。 */
export function addSheetModel(
  sheet: AddSheetState,
  facts: CartFacts,
): AddSheetModel {
  const { product, detail, selection, phase } = sheet;
  const variant = selectedVariant(detail, selection);
  const label =
    variant?.title ||
    (selection.every((value) => value !== undefined)
      ? selection.join(" / ")
      : "");
  const quantity = parseQuantity(sheet.qtyText);
  const cap = variant && facts.known[variant.id];
  const inCart = variant ? inCartOf(facts, variant.id) : 0;
  const room = variant ? roomOf(facts, variant.id) : MAX_QUANTITY;
  const missing = missingOptions(detail, selection);
  const soldOut =
    missing.length === 0 &&
    (!variant || !variant.available || (room === 0 && inCart === 0));
  const reason =
    missing.length > 0
      ? copy.widget_cart_select({ options: listOf(missing) })
      : soldOut
        ? copy.widget_cart_variant_sold_out({
            variant: label || product.title,
          })
        : room === 0
          ? copy.widget_cart_all_in_cart({ count: cap ?? inCart })
          : quantity === undefined
            ? copy.widget_cart_enter_quantity()
            : undefined;
  const busy = phase === "adding" || phase === "checking";
  const money = (cents: number | undefined) =>
    cents === undefined || !facts.lines
      ? undefined
      : formatPrice(
          (cents / 100).toFixed(2),
          facts.lines.currency,
          facts.locale,
        );

  const status: CartStatus =
    phase === "adding"
      ? { tone: "plain", text: copy.widget_cart_adding() }
      : phase === "checking"
        ? { tone: "plain", text: copy.widget_cart_checking() }
        : phase === "failed"
          ? { tone: "error", text: copy.widget_cart_add_failed() }
          : phase === "rejected"
            ? {
                tone: "error",
                text: sheet.message ?? copy.widget_cart_rejected(),
              }
            : phase === "unconfirmed"
              ? { tone: "error", text: copy.widget_cart_unconfirmed() }
              : reason !== undefined
                ? { tone: "info", text: reason }
                : {
                    tone: "plain",
                    text: label
                      ? copy.widget_cart_summary({
                          variant: label,
                          count: quantity ?? 1,
                        })
                      : copy.widget_cart_summary_default({
                          count: quantity ?? 1,
                        }),
                  };
  const total =
    variant && quantity && reason === undefined
      ? money(
          variant.price === undefined ? undefined : variant.price * quantity,
        )
      : undefined;
  const recheck = phase === "unconfirmed" || phase === "checking";
  const primary = {
    label:
      phase === "checking"
        ? copy.widget_cart_checking_short()
        : busy
          ? copy.widget_cart_adding_short()
          : recheck
            ? copy.widget_cart_check_again()
            : phase === "failed"
              ? copy.widget_cart_retry()
              : total
                ? copy.widget_cart_add_total({ total })
                : copy.widget_product_add(),
    disabled:
      busy || reason !== undefined || (recheck && sheet.before === undefined),
    busy,
    retry: recheck || phase === "failed",
  };
  const hint =
    sheet.note ??
    [
      cap !== undefined ? copy.widget_cart_available({ count: cap }) : "",
      inCart > 0 ? copy.widget_cart_in_cart({ count: inCart }) : "",
    ]
      .filter((part) => part !== "")
      .join(" · ");
  const options: CartOptionView[] = detail.options.map((option, index) => {
    const chosen = selection[index];
    return {
      name: option.name,
      heading:
        chosen === undefined
          ? option.name
          : copy.widget_cart_option_heading({
              name: option.name,
              value: chosen,
            }),
      grid:
        option.values.length >= 3 &&
        option.values.every((value) => value.length <= GRID_VALUE_LENGTH),
      values: option.values.map((value) => ({
        value,
        pressed: chosen === value,
        soldOut: valueSoldOut(detail, selection, index, value),
      })),
    };
  });
  return {
    variant,
    label,
    quantity,
    room,
    reason,
    soldOut,
    busy,
    title: product.title,
    price: (variant && money(variant.price)) ?? product.price,
    imageUrl:
      variant?.imageUrl ?? (product.hasImage ? product.imageUrl : undefined),
    options,
    hint,
    decreaseDisabled: !(quantity !== undefined && quantity > 1),
    increaseDisabled: soldOut || (quantity ?? 1) >= Math.max(1, room),
    status,
    primary,
    showCart: recheck,
    recheck,
  };
}

export interface EditSheetModel {
  quantity?: number;
  /** 数量框的上限：Shopify 说过的上限，没说过就按数量框的上限。 */
  max: number;
  /** Shopify 确认的这一行现在几件；还没读到就没有。 */
  current?: number;
  busy: boolean;
  /** 上次没改成：按钮按刚才要改成的数重来。 */
  retrySave: boolean;
  /** 这一行没读到：按钮重新读一次。 */
  reread: boolean;
  hint: string;
  decreaseDisabled: boolean;
  increaseDisabled: boolean;
  /** 购物车里读不到这一行：数量框也不能改。 */
  frozen: boolean;
  status: CartStatus;
  primary: Omit<CartPrimaryAction, "onClick">;
}

/** 改数量面板上显示的一切：由面板状态和购物车信息算出，不碰网络。 */
export function editSheetModel(
  sheet: EditSheetState,
  facts: CartFacts,
): EditSheetModel {
  const { receipt, line, phase } = sheet;
  const quantity = parseQuantity(sheet.draft);
  const cap = facts.known[receipt.variantId];
  const max = cap ?? MAX_QUANTITY;
  const current = line.status === "ok" ? line.quantity : undefined;
  const busy = phase === "saving";
  const status: CartStatus =
    phase === "saving"
      ? { tone: "plain", text: copy.widget_cart_updating() }
      : phase === "failed" && sheet.tried !== undefined && current !== undefined
        ? {
            tone: "error",
            text: copy.widget_cart_change_failed({
              to: sheet.tried,
              from: current,
            }),
          }
        : phase === "failed" || phase === "unconfirmed"
          ? { tone: "error", text: copy.widget_cart_change_unconfirmed() }
          : phase === "rejected"
            ? {
                tone: "error",
                text: sheet.message ?? copy.widget_cart_rejected(),
              }
            : line.status === "loading"
              ? { tone: "plain", text: copy.widget_cart_checking() }
              : line.status === "missing"
                ? { tone: "info", text: copy.widget_cart_missing() }
                : line.status === "failed"
                  ? { tone: "error", text: copy.widget_cart_read_failed() }
                  : quantity === undefined
                    ? { tone: "info", text: copy.widget_cart_enter_quantity() }
                    : quantity === current
                      ? { tone: "plain", text: copy.widget_cart_unchanged() }
                      : {
                          tone: "plain",
                          text: copy.widget_cart_changes({
                            from: current ?? receipt.quantity,
                            to: quantity,
                          }),
                        };
  const retrySave =
    (phase === "failed" || phase === "unconfirmed") &&
    sheet.tried !== undefined;
  const reread = line.status === "failed";
  const primary = {
    label: busy
      ? copy.widget_cart_updating_short()
      : retrySave
        ? copy.widget_cart_retry_set({ count: sheet.tried ?? 0 })
        : reread
          ? copy.widget_cart_retry()
          : copy.widget_cart_update(),
    disabled:
      busy ||
      (!retrySave &&
        !reread &&
        (line.status !== "ok" ||
          quantity === undefined ||
          quantity === current)),
    busy,
    retry: retrySave || reread,
  };
  return {
    quantity,
    max,
    current,
    busy,
    retrySave,
    reread,
    hint:
      sheet.note ??
      (cap !== undefined ? copy.widget_cart_available({ count: cap }) : ""),
    decreaseDisabled: !(quantity !== undefined && quantity > 1),
    increaseDisabled: (quantity ?? 1) >= max,
    frozen: line.status !== "ok",
    status,
    primary,
  };
}
